import React, { useState, useEffect, useCallback } from 'react';
import { TeamTimelinePlacement, EventTeam, TeamAppCard } from '../../types';
import './TeamAppOverview.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

interface TeamState {
  teamId: string;
  teamName: string;
  joinCode?: string;
  placements: TeamTimelinePlacement[];
  lastUpdated: string | null;
}

interface TeamAppOverviewProps {
  eventId: string;
  startYear?: number;
  onClose?: () => void;
}

const TeamAppOverview: React.FC<TeamAppOverviewProps> = ({
  eventId,
  startYear,
  onClose
}) => {
  const [teamStates, setTeamStates] = useState<TeamState[]>([]);
  const [currentRound, setCurrentRound] = useState<{ roundId: string; revealedCards: TeamAppCard[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  
  // Fetch team states
  const fetchTeamStates = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/team-app/event/${eventId}/states`);
      if (response.ok) {
        const data = await response.json();
        setTeamStates(data.teams || []);
        setCurrentRound(data.currentRound);
        setLastUpdate(new Date());
      }
    } catch (err) {
      console.error('Failed to fetch team states:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);
  
  // Initial fetch and polling
  useEffect(() => {
    fetchTeamStates();
    const interval = setInterval(fetchTeamStates, 2000);
    return () => clearInterval(interval);
  }, [fetchTeamStates]);
  
  // Get time since last update for a team
  const getTimeSince = (lastUpdated: string | null): string => {
    if (!lastUpdated) return 'Nog niet actief';
    const diff = Date.now() - new Date(lastUpdated).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s geleden`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m geleden`;
    return 'Lang geleden';
  };
  
  // Sort placements by position
  const sortPlacements = (placements: TeamTimelinePlacement[]): TeamTimelinePlacement[] => {
    return [...placements].sort((a, b) => a.position - b.position);
  };
  
  if (loading) {
    return (
      <div className="team-overview-container">
        <div className="team-overview-loading">
          <div className="loading-spinner"></div>
          <p>Team data laden...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="team-overview-container">
      <div className="team-overview-header">
        <h2>📱 Team App Overzicht</h2>
        <div className="team-overview-meta">
          <span className="team-count">{teamStates.length} teams</span>
          <span className="cards-count">
            {currentRound?.revealedCards?.length || 0} kaarten vrijgegeven
          </span>
          <span className="last-update">Update: {lastUpdate.toLocaleTimeString()}</span>
          {onClose && (
            <button onClick={onClose} className="close-btn">✕</button>
          )}
        </div>
      </div>
      
      <div className="team-overview-grid">
        {teamStates.map(team => {
          const sortedPlacements = sortPlacements(team.placements);
          const hasActivity = team.placements.length > 0;
          const isActive = team.lastUpdated && 
            (Date.now() - new Date(team.lastUpdated).getTime()) < 30000; // Active in last 30s
          
          return (
            <div 
              key={team.teamId} 
              className={`team-overview-card ${hasActivity ? 'has-activity' : ''} ${isActive ? 'is-active' : ''}`}
            >
              <div className="team-overview-card-header">
                <span className="team-name">{team.teamName}</span>
                <span className={`team-status ${isActive ? 'active' : ''}`}>
                  {isActive ? '🟢' : '⚪'}
                </span>
              </div>
              
              {team.joinCode && (
                <div className="team-code">Code: {team.joinCode}</div>
              )}
              
              <div className="team-timeline-view">
                {startYear && (
                  <div className="timeline-start">
                    <span className="start-label">Start</span>
                    <span className="start-year">{startYear}</span>
                  </div>
                )}
                
                <div className="timeline-slots">
                  {sortedPlacements.length > 0 ? (
                    sortedPlacements.map((placement, index) => (
                      <div key={placement.songId} className="timeline-slot filled">
                        <span className="slot-number">{index + 1}</span>
                      </div>
                    ))
                  ) : (
                    <div className="no-placements">
                      Nog geen kaarten geplaatst
                    </div>
                  )}
                </div>
              </div>
              
              <div className="team-last-update">
                {getTimeSince(team.lastUpdated)}
              </div>
            </div>
          );
        })}
        
        {teamStates.length === 0 && (
          <div className="team-overview-empty">
            <p>Geen teams met Team App ingeschakeld</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamAppOverview;
